import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { TreeViewCollapseIcon } from '@mui/x-tree-view/icons';
import { useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import CustomTooltip from './CustomTooltip';

type DropdownValue = string | number;

export type SuboptionedDropdownGroupItem = {
    type: 'group';
    id: string;
    label: string;
    testId?: string;
};

export type SuboptionedDropdownOptionItem<TValue extends DropdownValue> = {
    type: 'option';
    value: TValue;
    label: string;
    parentGroupId?: string;
    disabled?: boolean;
    testId?: string;
};

export type SuboptionedDropdownItem<TValue extends DropdownValue> = SuboptionedDropdownGroupItem | SuboptionedDropdownOptionItem<TValue>;

type SuboptionedDropdownProps<TValue extends DropdownValue> = {
    value: TValue;
    onChange: (value: TValue) => void;
    fieldLabel: string;
    fieldLabelTooltip?: ReactNode;
    disabled?: boolean;
    selectTestId?: string;
    items: SuboptionedDropdownItem<TValue>[];
};

const SuboptionedDropdown = <TValue extends DropdownValue>({
    value,
    onChange,
    fieldLabel,
    fieldLabelTooltip,
    disabled = false,
    selectTestId,
    items,
}: SuboptionedDropdownProps<TValue>) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [expandedGroupIds, setExpandedGroupIds] = useState<Record<string, boolean>>({});
    const keepMenuOpenRef = useRef(false);

    const options = useMemo(() => items.filter((item): item is SuboptionedDropdownOptionItem<TValue> => item.type === 'option'), [items]);
    const groups = useMemo(() => items.filter((item): item is SuboptionedDropdownGroupItem => item.type === 'group'), [items]);
    const selectedOption = useMemo(() => options.find((option) => option.value === value), [options, value]);

    const groupForSelectedValue = useMemo(() => {
        const parentGroupId = selectedOption?.parentGroupId;
        if (!parentGroupId) return undefined;
        return groups.find((group) => group.id === parentGroupId);
    }, [groups, selectedOption]);

    const toggleGroup = (groupId: string) => {
        keepMenuOpenRef.current = true;
        setExpandedGroupIds((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
    };

    const isValidOptionValue = (candidate: unknown): candidate is TValue => options.some((option) => option.value === candidate);

    const isGroupExpanded = (groupId: string): boolean => expandedGroupIds[groupId] ?? groupForSelectedValue?.id === groupId;

    return (
        <FormControl
            fullWidth
            size="small"
            sx={{
                flex: 1,
                minWidth: 0,
                '& .MuiSelect-select': {
                    fontSize: '8px',
                    padding: '5px 7px',
                },
                '& .MuiInputLabel-root': {
                    fontSize: '10px',
                },
            }}
        >
            <InputLabel>
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                    <TreeViewCollapseIcon sx={{ fontSize: '0.95rem' }} />
                    {fieldLabelTooltip ? (
                        <CustomTooltip side="right" mode="icon" title={fieldLabelTooltip}>
                            <Box component="span">{fieldLabel}</Box>
                        </CustomTooltip>
                    ) : (
                        fieldLabel
                    )}
                </Box>
            </InputLabel>
            <Select
                data-testid={selectTestId}
                value={value}
                label={fieldLabel}
                disabled={disabled}
                IconComponent={() => null}
                open={isMenuOpen}
                onOpen={() => setIsMenuOpen(true)}
                onClose={() => {
                    if (keepMenuOpenRef.current) {
                        keepMenuOpenRef.current = false;
                        return;
                    }
                    setIsMenuOpen(false);
                }}
                renderValue={() => selectedOption?.label ?? ''}
                sx={{
                    '& .MuiSelect-select': {
                        pr: '7px !important',
                        textOverflow: 'clip',
                    },
                }}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            '& .MuiMenuItem-root': {
                                fontSize: '11px',
                                minHeight: '24px',
                            },
                        },
                    },
                }}
                onChange={(e) => {
                    const nextValue = e.target.value;
                    if (!isValidOptionValue(nextValue)) return;
                    onChange(nextValue);
                }}
            >
                {items.map((item) => {
                    if (item.type === 'group') {
                        const expanded = isGroupExpanded(item.id);
                        return (
                            <MenuItem
                                key={`group-${item.id}`}
                                data-testid={item.testId}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    toggleGroup(item.id);
                                }}
                            >
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
                                    <Typography sx={{ fontSize: '11px' }}>{item.label}</Typography>
                                    <TreeViewCollapseIcon
                                        sx={{
                                            fontSize: '0.9rem',
                                            transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                                            transformOrigin: 'center',
                                            transition: 'transform 120ms ease',
                                        }}
                                    />
                                </Box>
                            </MenuItem>
                        );
                    }

                    const parentGroupId = item.parentGroupId;
                    if (parentGroupId && !isGroupExpanded(parentGroupId)) return null;

                    return (
                        <MenuItem
                            key={`option-${String(item.value)}`}
                            data-testid={item.testId}
                            value={item.value}
                            disabled={item.disabled}
                        >
                            <Box sx={{ pl: parentGroupId ? 2 : 0 }}>{item.label}</Box>
                        </MenuItem>
                    );
                })}
            </Select>
        </FormControl>
    );
};

export default SuboptionedDropdown;
